import React from 'react';

interface ScriptureQuoteProps {
  verse: string;
  reference: string;
  className?: string;
}

export const ScriptureQuote: React.FC<ScriptureQuoteProps> = ({ verse, reference, className = '' }) => {
  return (
    <div className={`scripture-quote ${className}`}>
      <blockquote className="font-sacred text-lg italic text-white-700 mb-4">
        "{verse}"
      </blockquote>
      <cite className="text-sm text-white-600 font-medium">
        — {reference}
      </cite>
    </div>
  );
};

interface CrossDecorationProps {
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const CrossDecoration: React.FC<CrossDecorationProps> = ({ 
  children, 
  size = 'md', 
  className = '' 
}) => {
  const sizeClasses = {
    sm: 'text-lg',
    md: 'text-2xl',
    lg: 'text-4xl'
  };

  return (
    <div className={`cross-decoration ${className}`}>
      <div className={`absolute -top-2 -left-2 text-gold-500 ${sizeClasses[size]} opacity-30`}>
        ✞
      </div>
      {children}
    </div>
  );
};

interface ChristianIconProps {
  icon: 'cross' | 'dove' | 'fish' | 'lamb' | 'vine' | 'olive';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const ChristianIcon: React.FC<ChristianIconProps> = ({ 
  icon, 
  size = 'md', 
  className = '' 
}) => {
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-12 h-12'
  };

  const icons = {
    cross: '',
    dove: '',
    fish: '',
    lamb: '',
    vine: '',
    olive: ''
  };

  return (
    <div className={`${sizeClasses[size]} flex items-center justify-center text-gold-600 ${className}`}>
      <span className="text-2xl">{icons[icon]}</span>
    </div>
  );
};

interface SacredSectionProps {
  children: React.ReactNode;
  title?: string;
  className?: string;
  id?: string; // Added id prop for section identification
}

export const SacredSection: React.FC<SacredSectionProps> = ({ 
  children, 
  title, 
  className = '',
  id
}) => {
  return (
    <section id={id} className={`sacred-border christian-gradient ${className}`}>
      {title && (
        <div className="text-center mb-8">
          <h2 className="font-scripture text-3xl font-bold text-primary-800 mb-2">
            {title}
          </h2>
          <div className="w-24 h-1 bg-gold-500 mx-auto"></div>
        </div>
      )}
      {children}
    </section>
  );
};

// Scripture verse keys for translation
export const scriptureKeys = {
  comfort: 'comfort',
  peace: 'peace', 
  eternal: 'eternal',
  hope: 'hope',
  love: 'love'
};

const christianElements = {
  ScriptureQuote,
  CrossDecoration,
  ChristianIcon,
  SacredSection,
  scriptureKeys
};

export default christianElements;
